import React, { useState } from 'react'
import { InputRangeOld } from '@blockComponents/InputRangeOld'
import { BlockAnchors } from '@constants/forms'
import { apiUrls } from '@http/api-urls'
import { httpService } from '@http/service'
import { Section } from '@shared/components/section'
import { Spoiler } from '@shared/components/spoiler'
import { Button } from '@shared/componentsStyled/Button'
import { ExternalPaths } from '@shared/menu-items/paths'
import { changeDocumentHash } from '@shared/pipes/dom'
import scrollToElement from '@shared/pipes/scroll'
import { formatCalcSum } from '@shared/utils/helpers'
import { useFormik } from 'formik'
import styled, { CSSObject } from 'styled-components'

import { Input } from '../Input'

export const goToCalculatorLink = () => {
	scrollToElement(`#${BlockAnchors.calculator}`, { time: 0 }, () => {
		changeDocumentHash(BlockAnchors.calculator)
	})
}

const NAME_MAX_LENGTH = 500
const START_VALUE = 500000
const THOUSAND_10 = 10000
const MILLION_1 = 1000000

export function SectionBuyCertificate(): JSX.Element {
	const { values, errors, status, handleChange, setFieldValue, isSubmitting, handleSubmit } =
		useFormik({
			initialValues: {
				rangeValue: START_VALUE,
				name: '',
				phone: '',
			},
			onSubmit: async (values, { setSubmitting, setStatus }) => {
				setSubmitting(true)

				const phone = values.phone.replace(/[^0-9]/g, '').slice(-10)
				const payload = {
					amount: values.rangeValue,
					name: values.name.trim(),
					phone,
				}

				try {
					await httpService.post(apiUrls.savingCertificatePurchase(), payload)

					setStatus('submitted')
				} finally {
					setSubmitting(false)
				}
			},
		})

	return (
		<>
			<Section id={BlockAnchors.calculator}>
				<ViewRoot>
					<TextTitle>Купить сертификат</TextTitle>
					<TextSub>Заполните поля ниже, проведите оплату и получите сертификат!</TextSub>
					<form onSubmit={handleSubmit}>
						<ViewContent>
							<ViewCalc>
								<InputRow>
									<TextLabel htmlFor='investFondAmount'>Сумма подарка</TextLabel>
									<InputRangeOld
										value={values.rangeValue}
										onChange={(value) => setFieldValue('rangeValue', value)}
										step={10000}
										max={MILLION_1}
										min={THOUSAND_10}
										formatLabel={(rangeValue) => `${formatCalcSum(+values.rangeValue)} рублей`}
										labelClassName='calcLabel'
										activeTrackClassName='calcTrack'
										inputRangeClassName='calcRange'
										resultClassName='calcResult'
									/>
								</InputRow>
								<ViewCalcRow>
									<ViewCalcCol>
										<TextLabel htmlFor='investGiftName'>Как к вам обращаться</TextLabel>
										<Input
											id='investGiftName'
											name='name'
											type='text'
											placeholder='Имя'
											onChange={handleChange}
											value={values.name}
											hasError={!!errors.name}
											maxLength={NAME_MAX_LENGTH}
										/>
									</ViewCalcCol>
								</ViewCalcRow>
								<ViewCalcRow>
									<ViewCalcCol>
										<TextLabel htmlFor='investGiftPhone'>Ваш номер телефона</TextLabel>
										<Input
											mask='+7 999 999-99-99'
											id='investGiftPhone'
											name='phone'
											value={values.phone}
											type='text'
											onChange={handleChange}
											placeholder='Телефон'
											hasError={!!errors.phone}
										/>
									</ViewCalcCol>
								</ViewCalcRow>
							</ViewCalc>
							<ViewRes>
								<ViewResRow>
									<TextLabelSmall>Как оплатить и получить сертификат?</TextLabelSmall>
								</ViewResRow>
								<ViewResList>
									<ViewResItem>
										<Number>1</Number>
										<div>Выберите сумму сертификата и заполните форму</div>
									</ViewResItem>
									<ViewResItem>
										<Number>2</Number>
										<div>
											Оплатите сертификат по QR коду, через СБП или иным безналичным способом
										</div>
									</ViewResItem>
									<ViewResItem>
										<Number>3</Number>
										<div>
											После получения оплаты с вами свяжется наш менеджер, чтобы предоставить
											электронный сертификат с кодом активации
										</div>
									</ViewResItem>
									<ViewResItem>
										<Number>4</Number>
										<div>
											Подарите сертификат, код для активации можно использовать только один раз
										</div>
									</ViewResItem>
								</ViewResList>
							</ViewRes>
						</ViewContent>
						<ViewAction>
							<StyledButton type='submit' success={status === 'submitted'} disabled={isSubmitting}>
								{status === 'submitted' ? 'Отправлено' : 'Перейти к оплате'}
							</StyledButton>
							<Description>
								Нажимая кнопку «Перейти к оплате» и оплачивая подарочный сертификат, плательщик
								выражает согласие на обслуживание в соответствии с опубликованным{' '}
								<a
									className='main-link'
									href='https://storage.yandexcloud.net/creditclub-frontend/static/docs/creditclub/bonus/gift_certificate.pdf '
									target='_blank'
									rel='noopener noreferrer nofollow'
								>
									Публичным договором-офертой «Подарочный сертификат на сбережения»
								</a>{' '}
								и выражает{' '}
								<a
									className='main-link'
									href='https://credit.club/agreement'
									target='_blank'
									rel='noopener noreferrer nofollow'
								>
									согласие с обработкой персональных данных
								</a>
							</Description>
						</ViewAction>
					</form>
				</ViewRoot>
			</Section>
			<Section>
				<ViewSpoiler>
					<Spoiler buttonText='Подробные условия' buttonTextAlign='right'>
						Реклама. Денежные средства по подарочному сертификату вносятся Дарителем (доверителем)
						на условиях Публичного договора-оферты «Подарочный сертификат на сбережения» №0504 от 04
						мая 2023 г., размещенной на{' '}
						<a
							className='main-link'
							href='https://credit.club'
							target='_blank'
							rel='noopener noreferrer nofollow'
						>
							https://credit.club
						</a>{' '}
						(Поверенный Общество с ограниченной ответственностью «Кредит Клаб.Консалт» 620075,
						Россия, Свердловская область, г. Екатеринбург, ул. Малышева, строение 51, офис 2507 ИНН
						668501001 ОГРН 1176658123223).
						<br />
						<br />
						Финансовые услуги физическим лицам оказывает Кредитный потребительский кооператив «Фонд
						Ипотечного Кредитования» (Свердловская область, г. Екатеринбург, ул. Малышева, стр. 51,
						оф. 26/14, ОГРН 1196658040677 ИНН 6678102434,{' '}
						<a
							className='main-link'
							href={ExternalPaths.loanfund}
							target='_blank'
							rel='noopener noreferrer nofollow'
						>
							https://loanfund.ru/
						</a>
						) в рамках Федерального закона № 190-ФЗ от 18.07.2009 года путем заключения договора
						передачи личных сбережений на следующих условиях: <br />
						• Сумма передаваемых личных сбережений от 10 000 до 40 000 000 рублей. <br />
						• Ставка процентов за пользование кооперативом привлечёнными денежными средствами члена
						Кооператива — от 4,5% до 12,5% годовых, определяется индивидуально исходя из выбранной
						программы привлечения денежных средств членов Кооператива. <br />
						• Минимальный срок возврата сбережений — от 3 рабочих дней до 24 месяцев определяется
						индивидуально исходя из выбранной программы. <br />
						• Максимальный срок возврата личных сбережений — от 6 до 60 месяцев. Выплата процентов
						осуществляется ежемесячно или при возврате суммы сбережений.
						<br />
						<br />
						Кредитный потребительский кооператив оказывает услуги исключительно членам кредитного
						кооператива (пайщикам). При вступлении в кооператив вносится обязательный паевой взнос в
						размере 50 рублей. Член кредитного кооператива (пайщик) обязаны нести субсидиарную
						ответственность по обязательствам кооператива в пределах невнесенной части
						дополнительного взноса каждого из членов кредитного кооператива (пайщиков).
						<br />
						<br />
						Повышение доходности осуществляется за счет присоединения к программе лояльности ООО
						«Кредит.Клаб», (Свердловская область, г. Екатеринбург, ул. Малышева, стр. 51, оф. 26/14,
						ОГРН 1196658084743 ИНН 6678105594 официальный сайт{' '}
						<a
							className='main-link'
							href='https://credit.club'
							target='_blank'
							rel='noopener noreferrer nofollow'
						>
							https://credit.club/
						</a>
						. Вознаграждение составляет от 1,5% до 3% годовых, из которого удерживается налог на
						доходы физических лиц по ставке 13% (пункт 1 статьи 224 НК РФ). Выплата вознаграждения
						осуществляется ежемесячно.
						<br />
						<br />
						Сервис цифровых решений предоставляется участником проекта инновационного центра
						«Сколково» ООО «Кредит.Клаб» (Свердловская область, г. Екатеринбург, ул. Малышева, стр.
						51, оф. 26/14, ОГРН 1196658084743 ИНН 6678105594,{' '}
						<a
							className='main-link'
							href='https://credit.club'
							target='_blank'
							rel='noopener noreferrer nofollow'
						>
							https://credit.club/
						</a>
						).
						<br />
						<br />
						Не является публичной офертой.
						<br />
						<br />
						Продукт «Сбережение» не относится к инвестициям, предусмотренным федеральным законом от
						02.08.2019 № 259-ФЗ «О привлечении инвестиций с использованием инвестиционных платформ и
						о внесении изменений в отдельные законодательные акты Российской Федерации»
					</Spoiler>
				</ViewSpoiler>
			</Section>
		</>
	)
}

const ViewRoot = styled.div(({ theme }) => ({
	border: '1px solid rgba(216, 234, 228, 0.25)',
	borderRadius: 40,
	boxShadow: '0px 10px 30px rgba(163, 198, 186, 0.25)',
	marginBottom: 15,
	padding: 20,
	[theme.mediaQueries.mediaMinWidthMobile]: {
		marginBottom: 40,
		padding: '40px 60px',
	},
}))

const TextTitle = styled.h2(({ theme }) => ({
	...theme.typography.header30_36,
	marginBottom: 20,
	[theme.mediaQueries.mediaMinWidthMobile]: {
		...theme.typography.header56_60,
		marginBottom: 20,
	},
}))

const TextSub = styled.div(({ theme }) => ({
	...theme.typography.text18_24,
	marginBottom: 20,
	[theme.mediaQueries.mediaMinWidthMobile]: {
		...theme.typography.text24_32,
		marginBottom: 40,
	},
}))

const ViewContent = styled.div(
	({ theme }) =>
		({
			display: 'flex',
			flexDirection: 'column',
			marginBottom: 30,
			[theme.mediaQueries.mediaMinWidthMobile]: {
				flexDirection: 'row',
			},
		} as CSSObject)
)

const ViewResList = styled.div(
	() =>
		({
			display: 'flex',
			flexDirection: 'column',
		} as CSSObject)
)

const Number = styled.div(
	({ theme }) =>
		({
			...theme.typography.text18_24,
			textAlign: 'center',
			display: 'block',
			color: 'white',
			backgroundColor: 'black',
			borderRadius: '50%',
			width: 24,
			height: 24,
			marginRight: 8,
		} as CSSObject)
)

const ViewCalc = styled.div(
	({ theme }) =>
		({
			flex: 1,
			marginRight: 0,
			display: 'flex',
			flexDirection: 'column',
			[theme.mediaQueries.mediaMinWidthMobile]: {
				marginRight: 80,
			},
			'& .calcLabel': {
				...theme.typography.text12_16,
			},
			'& .calcTrack': {
				backgroundColor: theme.colors.lightbrown,
			},
			'& .calcRange': {
				backgroundColor: theme.colors.peach,
			},
			'& .calcResult': {
				...theme.typography.text18_24,
				backgroundColor: theme.colors.lightmint,
				height: 40,
				paddingBottom: 4,
				[theme.mediaQueries.mediaMinWidthMobile]: {
					...theme.typography.text24_32,
					height: 64,
					paddingBottom: 0,
				},
			},
		} as CSSObject)
)

const InputRow = styled.div({
	marginBottom: 10,
})

const ViewRes = styled.div(
	({ theme }) =>
		({
			flex: 1,
			width: '100%',
			backgroundColor: theme.colors.lightmint,
			borderRadius: 8,
			height: 'min-content',
			padding: '10px 15px',
			display: 'flex',
			flexDirection: 'column',
			marginTop: 32,
			[theme.mediaQueries.mediaMinWidthMobile]: {
				padding: '20px 30px',
			},
		} as CSSObject)
)

const TextLabel = styled.label(({ theme }) => ({
	...theme.typography.text14_18,
	opacity: 0.5,
	display: 'block',
	marginBottom: 8,
	whiteSpace: 'normal',

	[theme.mediaQueries.mediaMinWidthMobile]: {
		...theme.typography.text18_24,
		marginBottom: 10,
		whiteSpace: 'nowrap',
	},
}))

const TextLabelSmall = styled.label(({ theme }) => ({
	...theme.typography.text14_18,
	opacity: 0.5,
	display: 'block',
	marginBottom: 5,
	whiteSpace: 'normal',

	[theme.mediaQueries.mediaMinWidthMobile]: {
		...theme.typography.text18_24,
		whiteSpace: 'nowrap',
	},
}))

const ViewCalcRow = styled.div(
	({ theme }) =>
		({
			display: 'flex',
			flexDirection: 'column',
			[theme.mediaQueries.mediaMinWidthMobile]: {
				flexDirection: 'row',
			},
		} as CSSObject)
)

const ViewCalcCol = styled.div(() => ({
	flex: 1,
	marginRight: 0,
	marginBottom: 20,
}))

const ViewResRow = styled.div(
	({ theme }) =>
		({
			display: 'flex',
			flexDirection: 'column',
			width: '100%',
			[theme.mediaQueries.mediaMinWidthMobile]: {
				flexDirection: 'row',
			},
		} as CSSObject)
)

const ViewResItem = styled.div(({ theme }) => ({
	...theme.typography.text18_24,
	display: 'grid',
	gridTemplateColumns: 'min-content auto',
	':not(:last-child)': {
		marginBottom: 8,
	},
}))

const ViewAction = styled.div(({ theme }) => ({
	display: 'grid',
	gridGap: 24,
	gridTemplateColumns: '1fr',
	[theme.mediaQueries.mediaMinWidthMobile]: {
		gridTemplateColumns: 'min-content auto',
	},
}))

const Description = styled.div(({ theme }) => ({
	...theme.typography.text12_16,
	opacity: 0.5,
	margin: 'auto',
}))

const StyledButton = styled(Button)<{ success: boolean }>(({ theme, success }) => ({
	...theme.typography.text16_22,
	'white-space': 'nowrap',
	height: 40,

	...Object.assign(
		{},
		success && {
			backgroundColor: '#49D4A7',

			'&:hover': {
				backgroundColor: '#49D4A7',
			},

			'&:active': {
				backgroundColor: '#49D4A7',
			},
		}
	),

	[theme.mediaQueries.mediaMinWidthMobile]: {
		...theme.typography.text24_32,
		height: 'fit-content',
		padding: '16px 30px',
	},
}))

const ViewSpoiler = styled.div(({ theme }) => ({
	...theme.typography.text12_16,
	opacity: 0.5,
	marginBottom: 28,
}))
